import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import {
  Users,
  Box,
  FileSignature,
  Receipt,
  Banknote,
  Wallet,
  Undo2,
  Scale,
  Activity,
  Megaphone,
  ScrollText,
  ClipboardList,
  Eye,
  type LucideIcon,
} from 'lucide-react';
import { Eyebrow } from '@/components/ui/eyebrow';
import { cn } from '@/lib/utils';

/**
 * Subnav — handoff COMPONENTS §10
 * Agrupada por eyebrow MENSAL / ANUAL, com ícones + counts
 */

interface SubnavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  count?: number;
}

const mensal: SubnavItem[] = [
  { to: '/lancamentos/pagamentos', label: 'Pagamentos', icon: Receipt },
  { to: '/lancamentos/empregados', label: 'Empregados', icon: Users },
  { to: '/lancamentos/bens', label: 'Bens', icon: Box },
  { to: '/lancamentos/contratos', label: 'Contratos', icon: FileSignature },
  { to: '/lancamentos/documentos-fiscais', label: 'Doc. Fiscais', icon: ScrollText },
  { to: '/lancamentos/receitas', label: 'Receitas', icon: Banknote },
  { to: '/lancamentos/disponibilidades', label: 'Disponibilidades', icon: Wallet },
  { to: '/lancamentos/devolucoes', label: 'Devoluções', icon: Undo2 },
  { to: '/lancamentos/ajustes', label: 'Ajustes', icon: Scale },
  { to: '/lancamentos/atividades', label: 'Atividades', icon: Activity },
];

const anual: SubnavItem[] = [
  { to: '/lancamentos/publicacoes', label: 'Publicações', icon: Megaphone },
  { to: '/lancamentos/declaracoes', label: 'Declarações', icon: ClipboardList },
  { to: '/lancamentos/demonstracoes', label: 'Demonstrações', icon: FileSignature },
  { to: '/lancamentos/transparencia', label: 'Transparência', icon: Eye },
];

export const Route = createFileRoute('/_app/lancamentos')({
  component: LancamentosLayout,
});

function SubnavGroup({ title, items }: { title: string; items: SubnavItem[] }) {
  const { pathname } = useLocation();
  return (
    <div className="flex flex-col gap-1">
      <Eyebrow className="px-3 pb-1.5">{title}</Eyebrow>
      {items.map((item) => {
        const Icon = item.icon;
        const active = pathname === item.to || pathname.startsWith(item.to + '/');
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'group flex h-9 items-center gap-2.5 rounded-[12px] px-3 text-[13px] font-medium transition-all duration-fast',
              active
                ? 'bg-card text-fg shadow-1 border border-card-stroke'
                : 'text-fg-muted hover:text-fg hover:bg-card-soft border border-transparent',
            )}
          >
            <Icon
              className={cn('h-4 w-4 shrink-0', active ? 'text-mint-deep' : 'text-fg-muted')}
              strokeWidth={1.8}
            />
            <span className="flex-1 truncate">{item.label}</span>
            {item.count !== undefined && item.count > 0 && (
              <span
                className={cn(
                  'font-mono text-[11px] font-semibold',
                  active ? 'text-mint-deep' : 'text-fg-dim',
                )}
              >
                {item.count}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

function LancamentosLayout() {
  return (
    <div className="flex gap-6">
      <nav className="hidden md:flex w-[220px] shrink-0 flex-col gap-4 rounded-3 bg-card-soft p-2.5 shadow-pressed h-fit sticky top-20 border border-card-stroke/40">
        <SubnavGroup title="Mensal" items={mensal} />
        <div className="h-px bg-card-stroke" />
        <SubnavGroup title="Anual" items={anual} />
      </nav>
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
